<template>
    <div class="devices-container">
        <!-- Stats Cards -->
        <div class="stats-grid">
            <div class="stat-card">
                <div class="stat-icon" style="background: #e0e7ff; color: #4f46e5;">
                    <i class="fas fa-server"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ allDevices.length }}</div>
                    <div class="stat-label">Total Devices</div>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon" style="background: #dcfce7; color: #16a34a;">
                    <i class="fas fa-folder-open"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ devicesWithGroup }}</div>
                    <div class="stat-label">Dans un groupe</div>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon" style="background: #fef9c3; color: #ca8a04;">
                    <i class="fas fa-inbox"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ devicesWithoutGroup }}</div>
                    <div class="stat-label">Sans groupe</div>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon" style="background: #dbeafe; color: #2563eb;">
                    <i class="fas fa-layer-group"></i>
                </div>
                <div class="stat-content">
                    <div class="stat-value">{{ availableGroups.length }}</div>
                    <div class="stat-label">Groupes actifs</div>
                </div>
            </div>
        </div>

        <!-- Filters and Actions Bar -->
        <div class="filters-bar">
            <div class="filters-left">
                <div class="search-box">
                    <i class="fas fa-search"></i>
                    <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Rechercher un device..."
                    >
                </div>
                <select class="filter-select" v-model="filterGroup">
                    <option value="">Tous les groupes</option>
                    <option value="null">Sans groupe</option>
                    <option v-for="group in availableGroups" :key="'filter-' + group.id" :value="group.id">
                        {{ group.name }}
                    </option>
                </select>
            </div>
            <div class="filters-right">
                <button
                    class="btn-bulk"
                    :class="{ active: bulkMode }"
                    @click="toggleBulkMode"
                    v-if="filteredDevices.length > 0"
                >
                    <i :class="bulkMode ? 'fas fa-times' : 'fas fa-check-square'"></i>
                    {{ bulkMode ? 'Quitter le mode bulk' : 'Mode bulk' }}
                </button>
            </div>
        </div>

        <!-- Bulk Assignment Bar -->
        <div class="bulk-bar" v-if="bulkMode && selectedDevices.length > 0">
            <div class="bulk-info">
                <i class="fas fa-check-square"></i>
                <strong>{{ selectedDevices.length }} device(s) sélectionné(s)</strong>
            </div>
            <div class="bulk-actions">
                <select class="bulk-select" v-model="bulkGroupId">
                    <option value="">Choisir un groupe...</option>
                    <option value="null">Retirer du groupe</option>
                    <option v-for="group in availableGroups" :key="'bulk-' + group.id" :value="group.id">
                        {{ group.name }}
                    </option>
                </select>
                <button
                    class="btn-apply"
                    @click="applyBulkAssignment"
                    :disabled="!bulkGroupId || isProcessing"
                >
                    <i class="fas fa-save"></i>
                    Appliquer
                </button>
            </div>
        </div>

        <!-- Table Card -->
        <div class="table-card">
            <div class="table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th v-if="bulkMode" class="checkbox-column">
                                <input
                                    type="checkbox"
                                    class="checkbox-input"
                                    @change="toggleSelectAll"
                                    :checked="isAllSelected"
                                >
                            </th>
                            <th class="color-column">Couleur</th>
                            <th>Device</th>
                            <th>Affichage</th>
                            <th>IMEI</th>
                            <th>Dernière connexion</th>
                            <th>Groupe actuel</th>
                            <th>Nouveau groupe</th>
                            <th class="actions-column">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="isLoading">
                            <td :colspan="bulkMode ? 9 : 8" class="loading-cell">
                                <div class="loading-spinner">
                                    <div class="spinner"></div>
                                    <p>Chargement...</p>
                                </div>
                            </td>
                        </tr>
                        <tr v-else-if="filteredDevices.length === 0">
                            <td :colspan="bulkMode ? 9 : 8" class="empty-cell">
                                <div class="empty-state">
                                    <i class="fas fa-inbox"></i>
                                    <p>Aucun device trouvé</p>
                                </div>
                            </td>
                        </tr>
                        <tr
                            v-for="device in paginatedDevices"
                            :key="'device-' + device.id"
                            class="table-row"
                            :class="{ selected: selectedDevices.includes(device.id) }"
                        >
                            <td v-if="bulkMode" class="checkbox-cell">
                                <input
                                    type="checkbox"
                                    class="checkbox-input"
                                    :value="device.id"
                                    v-model="selectedDevices"
                                >
                            </td>
                            <td class="color-cell">
                                <div
                                    class="color-indicator"
                                    :style="{ backgroundColor: device.color || '#9ca3af' }"
                                    :title="device.color || 'Pas de couleur'"
                                ></div>
                            </td>
                            <td>
                                <input
                                    type="text"
                                    class="device-name-input"
                                    v-model="device.pendingName"
                                    :placeholder="device.name"
                                    :disabled="bulkMode"
                                >
                            </td>
                            <td>
                                <input
                                    type="text"
                                    class="device-name-input"
                                    v-model="device.pendingDisplayName"
                                    :placeholder="device.display_name"
                                    :disabled="bulkMode"
                                >
                            </td>
                            <td>
                                <code class="imei-code">{{ device.imei }}</code>
                            </td>
                            <td>
                                <span class="last-connection">
                                    {{ getTimeSinceLastConnection(device) }}
                                </span>
                            </td>
                            <td>
                                <span v-if="device.group" class="group-badge">
                                    <i class="fas fa-folder"></i>
                                    {{ device.group.name }}
                                </span>
                                <span v-else class="no-group-badge">
                                    <i class="fas fa-inbox"></i>
                                    Sans groupe
                                </span>
                            </td>
                            <td>
                                <select
                                    class="group-select"
                                    :value="device.pendingGroupId !== undefined ? device.pendingGroupId : device.group_id"
                                    @change="setPendingGroup(device, $event.target.value)"
                                    :disabled="bulkMode"
                                >
                                    <option :value="null">-- Sans groupe --</option>
                                    <option
                                        v-for="group in availableGroups"
                                        :key="'select-' + device.id + '-' + group.id"
                                        :value="group.id"
                                    >
                                        {{ group.name }}
                                    </option>
                                </select>
                            </td>
                            <td class="actions-cell">
                                <button
                                    class="action-btn"
                                    :class="{ 'has-changes': hasChanges(device) }"
                                    @click="saveDeviceChanges(device)"
                                    :disabled="isProcessing || !hasChanges(device)"
                                    title="Enregistrer"
                                >
                                    <i class="fas fa-save"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div class="pagination" v-if="filteredDevices.length > pageSize">
                <div class="pagination-info">
                    Affichage de {{ startIndex + 1 }} à {{ Math.min(endIndex, filteredDevices.length) }}
                    sur {{ filteredDevices.length }} devices
                </div>
                <div class="pagination-controls">
                    <button
                        class="pagination-btn"
                        :disabled="currentPage === 1"
                        @click="currentPage--"
                    >
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <span class="pagination-pages">
                        Page {{ currentPage }} / {{ totalPages }}
                    </span>
                    <button
                        class="pagination-btn"
                        :disabled="currentPage === totalPages"
                        @click="currentPage++"
                    >
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import fetch from '@/utils/fetch';
import { useToast } from 'vue-toastification';

export default {
    name: 'DevicesTable',
    data() {
        return {
            availableGroups: [],
            allDevices: [],
            isLoading: false,
            isProcessing: false,
            searchQuery: '',
            filterGroup: '',
            bulkMode: false,
            selectedDevices: [],
            bulkGroupId: '',
            currentPage: 1,
            pageSize: 30
        };
    },
    computed: {
        filteredDevices() {
            let devices = [...this.allDevices];

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                devices = devices.filter(d =>
                    (d.name || '').toLowerCase().includes(query) ||
                    (d.display_name || '').toLowerCase().includes(query) ||
                    (d.imei || '').toLowerCase().includes(query)
                );
            }

            if (this.filterGroup !== '') {
                if (this.filterGroup === 'null') {
                    devices = devices.filter(d => !d.group_id);
                } else {
                    devices = devices.filter(d => d.group_id == this.filterGroup);
                }
            }

            return devices;
        },
        devicesWithGroup() {
            return this.allDevices.filter(d => d.group_id).length;
        },
        devicesWithoutGroup() {
            return this.allDevices.filter(d => !d.group_id).length;
        },
        isAllSelected() {
            return this.filteredDevices.length > 0 &&
                this.selectedDevices.length === this.filteredDevices.length;
        },
        totalPages() {
            return Math.ceil(this.filteredDevices.length / this.pageSize);
        },
        startIndex() {
            return (this.currentPage - 1) * this.pageSize;
        },
        endIndex() {
            return this.startIndex + this.pageSize;
        },
        paginatedDevices() {
            return this.filteredDevices.slice(this.startIndex, this.endIndex);
        }
    },
    mounted() {
        this.loadData();
    },
    methods: {
        async loadData() {
            this.isLoading = true;
            try {
                const groupsResponse = await fetch({ method: 'GET', url: 'device-groups' });
                this.availableGroups = groupsResponse.data.data || groupsResponse.data;

                const devicesResponse = await fetch({ method: 'GET', url: 'devices?lastevent=true' });
                this.allDevices = (devicesResponse.data.data || devicesResponse.data).map(d => ({
                    ...d,
                    pendingGroupId: undefined,
                    pendingName: d.name,
                    pendingDisplayName: d.display_name
                }));
            } catch (error) {
                console.error('Erreur lors du chargement:', error);
                const toast = useToast();
                toast.error('Erreur lors du chargement des données');
            } finally {
                this.isLoading = false;
            }
        },

        setPendingGroup(device, value) {
            const groupId = value === 'null' || value === '' ? null : parseInt(value);
            device.pendingGroupId = groupId;
            this.$forceUpdate();
        },

        hasChanges(device) {
            const groupChanged = device.pendingGroupId !== undefined && device.pendingGroupId !== device.group_id;
            const nameChanged = device.pendingName && device.pendingName.trim() !== device.name;
            const displayNameChanged = device.pendingDisplayName && device.pendingDisplayName.trim() !== device.display_name;
            return groupChanged || nameChanged || displayNameChanged;
        },

        async saveDeviceChanges(device) {
            this.isProcessing = true;
            const toast = useToast();

            try {
                const updateData = {};
                if (device.pendingGroupId !== undefined) updateData.group_id = device.pendingGroupId;
                if (device.pendingName && device.pendingName.trim() !== device.name) {
                    updateData.name = device.pendingName.trim();
                }
                if (device.pendingDisplayName && device.pendingDisplayName.trim() !== device.display_name) {
                    updateData.display_name = device.pendingDisplayName.trim();
                }

                await fetch({
                    method: 'PUT',
                    url: `devices/${device.id}`,
                    data: updateData
                });

                device.name = updateData.name || device.name;
                device.display_name = updateData.display_name || device.display_name;
                device.group_id = updateData.group_id !== undefined ? updateData.group_id : device.group_id;
                device.pendingGroupId = undefined;
                device.pendingName = device.name;
                device.pendingDisplayName = device.display_name;

                if (device.group_id) {
                    device.group = this.availableGroups.find(g => g.id === device.group_id);
                } else {
                    device.group = null;
                }

                toast.success(`Device ${device.name} mis à jour !`);
                this.$emit('devices-updated');
            } catch (error) {
                console.error('Erreur:', error);
                toast.error('Erreur lors de la mise à jour');
            } finally {
                this.isProcessing = false;
            }
        },

        toggleBulkMode() {
            this.bulkMode = !this.bulkMode;
            this.selectedDevices = [];
            this.bulkGroupId = '';
        },

        toggleSelectAll(e) {
            if (e.target.checked) {
                this.selectedDevices = this.filteredDevices.map(d => d.id);
            } else {
                this.selectedDevices = [];
            }
        },

        async applyBulkAssignment() {
            if (!this.bulkGroupId && this.bulkGroupId !== 'null') {
                const toast = useToast();
                toast.warning('Veuillez sélectionner un groupe');
                return;
            }

            this.isProcessing = true;
            const toast = useToast();
            const groupId = this.bulkGroupId === 'null' ? null : parseInt(this.bulkGroupId);

            try {
                for (const deviceId of this.selectedDevices) {
                    await fetch({
                        method: 'PUT',
                        url: `devices/${deviceId}`,
                        data: { group_id: groupId }
                    });
                }

                toast.success(`${this.selectedDevices.length} device(s) mis à jour !`);
                await this.loadData();
                this.toggleBulkMode();
                this.$emit('devices-updated');

            } catch (error) {
                console.error('Erreur lors de l\'assignation en masse:', error);
                toast.error('Erreur lors de l\'assignation en masse');
            } finally {
                this.isProcessing = false;
            }
        },

        getTimeSinceLastConnection(device) {
            // Essayer latest_event d'abord, puis eventsdata[0]
            const latestEvent = device.latest_event || (device.eventsdata && device.eventsdata[0]);

            if (!latestEvent) {
                return '⚪ Jamais';
            }

            // Essayer différents champs de date
            let dateStr = latestEvent.received_date ||
                         latestEvent.created_at ||
                         latestEvent.updated_at;

            if (!dateStr) {
                return '⚪ Jamais';
            }

            const lastConnection = new Date(dateStr);

            // Si la date est invalide
            if (isNaN(lastConnection.getTime())) {
                return '⚪ Inconnue';
            }

            // Formater la date : JJ/MM/AAAA HH:MM:SS
            const day = String(lastConnection.getDate()).padStart(2, '0');
            const month = String(lastConnection.getMonth() + 1).padStart(2, '0');
            const year = lastConnection.getFullYear();
            const hours = String(lastConnection.getHours()).padStart(2, '0');
            const minutes = String(lastConnection.getMinutes()).padStart(2, '0');
            const seconds = String(lastConnection.getSeconds()).padStart(2, '0');

            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        }
    },
    watch: {
        searchQuery() { this.currentPage = 1; },
        filterGroup() { this.currentPage = 1; }
    }
};
</script>

<style scoped>
.devices-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1rem;
}

.stat-card {
    background: white;
    border-radius: 0.75rem;
    padding: 1.25rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 56px;
    height: 56px;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    flex-shrink: 0;
}

.stat-content {
    flex: 1;
}

.stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    line-height: 1;
    margin-bottom: 0.25rem;
}

.stat-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
}

/* Filters Bar */
.filters-bar {
    background: white;
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filters-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
}

.search-box {
    display: flex;
    align-items: center;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    min-width: 250px;
}

.search-box i {
    color: #9ca3af;
    margin-right: 0.5rem;
}

.search-box input {
    border: none;
    background: transparent;
    outline: none;
    flex: 1;
    font-size: 0.875rem;
    color: #111827;
}

.search-box input::placeholder {
    color: #9ca3af;
}

.filter-select {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    font-size: 0.875rem;
    background: white;
    color: #374151;
    cursor: pointer;
    outline: none;
}

.filter-select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.btn-bulk {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: white;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    padding: 0.625rem 1.25rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-bulk:hover {
    background: #f9fafb;
    border-color: #d1d5db;
}

.btn-bulk.active {
    background: #fee2e2;
    color: #dc2626;
    border-color: #fca5a5;
}

/* Bulk Bar */
.bulk-bar {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
}

.bulk-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #1e40af;
    font-size: 0.875rem;
}

.bulk-info i {
    font-size: 1rem;
}

.bulk-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.bulk-select {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    font-size: 0.875rem;
    background: white;
    color: #374151;
    cursor: pointer;
    outline: none;
    min-width: 200px;
}

.btn-apply {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #16a34a;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-apply:hover:not(:disabled) {
    background: #15803d;
}

.btn-apply:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Table Card */
.table-card {
    background: white;
    border-radius: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.table-wrapper {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table thead {
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
}

.data-table th {
    padding: 0.75rem 1.5rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
}

.data-table th.checkbox-column,
.data-table th.color-column {
    width: 60px;
    text-align: center;
}

.data-table th.actions-column {
    text-align: right;
    width: 100px;
}

.data-table td {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: middle;
}

.data-table tbody tr:hover {
    background: #f9fafb;
}

.data-table tbody tr:last-child td {
    border-bottom: none;
}

.data-table tbody tr.selected {
    background: #eff6ff;
}

.checkbox-cell {
    text-align: center;
}

.checkbox-input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #4f46e5;
}

.color-cell {
    text-align: center;
}

.color-indicator {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: inline-block;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.device-name-input {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    width: 100%;
    outline: none;
    transition: all 0.2s;
}

.device-name-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.device-name-input:disabled {
    background: #f9fafb;
    color: #9ca3af;
}

.imei-code {
    background: #f3f4f6;
    color: #6b7280;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-family: 'Monaco', 'Menlo', monospace;
}

.last-connection {
    font-size: 0.75rem;
    color: #374151;
    white-space: nowrap;
}

.group-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    background: #e0e7ff;
    color: #4f46e5;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
}

.no-group-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    background: #f3f4f6;
    color: #6b7280;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
}

.group-select {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    font-size: 0.875rem;
    background: white;
    color: #374151;
    cursor: pointer;
    outline: none;
    min-width: 180px;
}

.group-select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.group-select:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
}

.actions-cell {
    text-align: right;
}

.action-btn {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    background: white;
    color: #d1d5db;
    cursor: not-allowed;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.action-btn.has-changes {
    border-color: #16a34a;
    background: #16a34a;
    color: white;
    cursor: pointer;
}

.action-btn.has-changes:hover:not(:disabled) {
    background: #15803d;
}

.action-btn:disabled {
    cursor: not-allowed;
}

.loading-cell {
    padding: 0 !important;
}

.loading-spinner {
    padding: 4rem 2rem;
    text-align: center;
    color: #9ca3af;
}

.spinner {
    width: 40px;
    height: 40px;
    margin: 0 auto 1rem;
    border: 3px solid #e5e7eb;
    border-top-color: #4f46e5;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.loading-spinner p {
    font-size: 0.875rem;
    margin: 0;
}

.empty-cell {
    padding: 0 !important;
}

.empty-state {
    padding: 4rem 2rem;
    text-align: center;
    color: #9ca3af;
}

.empty-state i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
}

.empty-state p {
    font-size: 0.875rem;
    margin: 0;
}

/* Pagination */
.pagination {
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fafafa;
}

.pagination-info {
    font-size: 0.875rem;
    color: #6b7280;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.pagination-btn {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    background: white;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
}

.pagination-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.pagination-pages {
    font-size: 0.875rem;
    color: #374151;
    font-weight: 500;
    padding: 0 0.75rem;
}

/* Responsive */
@media (max-width: 1024px) {
    .filters-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .filters-left {
        flex-direction: column;
        align-items: stretch;
    }

    .search-box {
        min-width: 100%;
    }

    .bulk-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .bulk-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .bulk-select {
        min-width: 100%;
    }
}

@media (max-width: 768px) {
    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .data-table th,
    .data-table td {
        padding: 0.75rem 1rem;
        font-size: 0.875rem;
    }

    .group-select {
        min-width: 140px;
    }

    .pagination {
        flex-direction: column;
        gap: 1rem;
    }
}

@media (max-width: 640px) {
    .stats-grid {
        grid-template-columns: 1fr;
    }

    .filters-bar {
        padding: 1rem;
    }
}
</style>
